import _extends from '@babel/runtime/helpers/extends';
import React__default, { useMemo, useCallback } from 'react';
import { S as StyledTree, A as ActionBarWrapper, O as OverrideWrapper, a as StyledTreeList, b as OuterNode, I as InnerNode, R as RenderNodeWrapper } from './Tree.styles-ClEBu9AC.js';
import { A as ArrowButton, N as NoArrowSpacer } from './ArrowButton-DhIA-TxH.js';
import { D as DepthSpacer } from './DepthSpacer-DrI8pGtD.js';
import { u as useStateEphemeral } from '../LocalModules/storm-ui-utils/dist/esm/useStateEphemeral-CrPQBxu_-CrPQBxu_.js';
import { T as TaktIdProvider } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { T as Text } from '../Text/Text-DvcofqkP.js';

const renderTreeNode = item => {
  if (item.renderLabel) return item.renderLabel(item.label);
  return /*#__PURE__*/React__default.createElement(Text, null, item.label);
};
const shouldArrowShow = (item, displayed) => {
  let areChildrenDisplayed = false;
  if (item.children && item.children.length > 0) {
    areChildrenDisplayed = true;
    if (displayed) {
      areChildrenDisplayed = false;
      for (let i = 0; i < item.children.length; i++) {
        if (displayed[item.children[i].id] !== undefined) {
          areChildrenDisplayed = true;
          break;
        }
      }
    }
  }
  return areChildrenDisplayed;
};
const getDisplayedChildrenNodes = (currentDisplayed = {}, node, displayAll) => {
  const childrenToDisplay = [];
  if (node.children && node.children.length) {
    const hasVisible = node.children.some(child => (currentDisplayed === null || currentDisplayed === void 0 ? void 0 : currentDisplayed[child.id]) === true);
    node.children.forEach(child => {
      if (displayAll || hasVisible) {
        childrenToDisplay.push(child);
      }
    });
  }
  return childrenToDisplay;
};
function Tree(props) {
  const {
    taktId,
    taktValue
  } = props;
  const {
    data,
    role = 'tree',
    displayed,
    renderNode,
    onDisplayChange,
    arrowLabel,
    depth = 0,
    maxDepth = Infinity,
    isLastTree,
    displayAll,
    renderActionBar,
    renderOverrideTree,
    ...rest
  } = props;
  // gives display control to the local Tree component if the `displayed` prop is used
  const [displayState, setDisplayState] = useStateEphemeral({}, {
    externalState: displayed,
    externalDispatch: onDisplayChange,
    useExternalState: !!displayed
  });
  // the display state must be combined with the root nodes of the tree if no external display state is used
  const displayedNodes = useMemo(() => displayed !== null && displayed !== void 0 ? displayed : data.reduce((acc, topNode) => ({
    ...acc,
    [topNode.id]: true
  }), displayState), [data, displayState, displayed]);
  const isRoot = depth === 0;
  const handleArrowClick = useCallback(item => {
    const displayedChildrenNodes = {};
    if (item.children) {
      for (let i = 0; i < ((_item$children = item.children) === null || _item$children === void 0 ? void 0 : _item$children.length); i++) {
        var _item$children;
        const hasVisible = item.children.some(node => (displayedNodes === null || displayedNodes === void 0 ? void 0 : displayedNodes[node.id]) === true);
        // if displayed is controlled externally
        if (displayed) {
          if (displayed[item.children[i].id] !== undefined) {
            displayedChildrenNodes[item.children[i].id] = !hasVisible;
          }
        } else {
          displayedChildrenNodes[item.children[i].id] = !hasVisible;
        }
      }
    }
    setDisplayState({
      ...displayedNodes,
      ...displayedChildrenNodes
    });
  }, [displayed, displayedNodes, setDisplayState]);
  const showArrows = data.map(item => shouldArrowShow(item, displayed) && depth < maxDepth && !displayAll);
  const hasChildWithArrowButton = showArrows.some(Boolean);
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: isRoot ? taktId : undefined,
    taktValue: isRoot ? taktValue : undefined,
    fallbackId: isRoot ? createStormTaktId('tree') : undefined
  }, /*#__PURE__*/React__default.createElement(StyledTree, _extends({
    role: role
  }, rest), renderActionBar && /*#__PURE__*/React__default.createElement(ActionBarWrapper, null, renderActionBar()), renderOverrideTree ? /*#__PURE__*/React__default.createElement(OverrideWrapper, null, renderOverrideTree()) : data.map((item, index) => {
    const displayedChildren = getDisplayedChildrenNodes(displayedNodes, item, displayAll);
    return displayedNodes && displayedNodes[item.id] && /*#__PURE__*/React__default.createElement(StyledTreeList, {
      "data-test-id": role === 'tree' ? `option-root-${item.label}` : undefined,
      role: "treeitem",
      key: item.id,
      $root: role === 'tree',
      $displayed: displayedChildren.length > 0
    }, /*#__PURE__*/React__default.createElement(OuterNode, {
      $root: role === 'tree',
      "data-test-id": `option-wrapper-${item.label}`
    }, /*#__PURE__*/React__default.createElement(DepthSpacer, {
      depth: depth,
      displayAll: displayAll
    }), /*#__PURE__*/React__default.createElement(InnerNode, null, showArrows[index] ? /*#__PURE__*/React__default.createElement(ArrowButton, {
      item: item,
      opened: displayedChildren.length > 0,
      onArrowClick: handleArrowClick,
      ariaLabel: typeof arrowLabel === 'string' ? arrowLabel : arrowLabel(item.label),
      taktId: taktId
    }) : /*#__PURE__*/React__default.createElement(NoArrowSpacer, {
      displayAll: displayAll,
      hasSiblingWithArrowButton: hasChildWithArrowButton
    }), /*#__PURE__*/React__default.createElement(RenderNodeWrapper, null, renderNode ? renderNode(item) : renderTreeNode(item)))), item.children && displayedChildren.length > 0 && /*#__PURE__*/React__default.createElement(Tree, _extends({}, rest, {
      data: displayedChildren,
      role: "group",
      displayed: displayed,
      renderNode: renderNode,
      onDisplayChange: onDisplayChange,
      arrowLabel: arrowLabel,
      depth: depth + 1,
      maxDepth: maxDepth,
      isLastTree: index === data.length - 1,
      displayAll: displayAll
    })));
  })));
}

export { Tree as T };
//# sourceMappingURL=Tree-ByeMI8XU.js.map
